/**
 * Internal Sales List Slice
 * 
 * ⚠️ WARNING: This Redux slice connects to INTERNAL backend endpoints with Basic Authentication.
 * Use only for internal monitoring/reporting. Do NOT use for standard user-facing features.
 * Auth: Basic Authentication (validated by backend)
 */

import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import {
  SalesApiResponse,
  SalesReservation,
  SalesState,
  SaleRow,
  SalesParams,
  SalesTraveler,
} from "app/types/service-types/SalesTypes";
import { InternalSalesService } from "app/services/internal-sales.services";
import { errorMsg } from "app/utils/MessageUtils";

const COMMISSION_RATE = 0.02;

const initialState: SalesState = {
  loading: false,
  error: null,
  response: null,
  rows: [],
};

function flightLabelFromCodes(
  codes?: SalesTraveler["departureFlightAirportCodes"]
): string {
  if (!codes) return "-";
  const dep = codes.departureAirportCode || "";
  const arr = codes.returnAirportCode || "";
  return dep && arr ? `${dep}-${arr}` : "-";
}

function toRows(reservations: SalesReservation[]): SaleRow[] {
  const rows: SaleRow[] = [];
  for (const r of reservations) {
    for (const tr of r.travelers) {
      const depPrice = Number(tr.baseDeparturePrice ?? 0);
      const retPrice = Number(tr.baseReturningPrice ?? 0);
      const seat = Number(tr.seatSelectionCost ?? 0);
      const baggage = Number(tr.baggageCost ?? 0);

      const total = depPrice + retPrice + seat + baggage;

      const commission =
        tr.commission != null
          ? Number(tr.commission)
          : r.commission != null
          ? Number(r.commission)
          : +(total * COMMISSION_RATE).toFixed(2);

      const netAmount =
        tr.netAmount != null
          ? Number(tr.netAmount)
          : r.netAmount != null
          ? Number(r.netAmount)
          : +(total - commission).toFixed(2);

      rows.push({
        id: tr.id,
        passenger: `${tr.name ?? ""} ${tr.surname ?? ""}`.trim() || "-",
        departureFlightLabel: flightLabelFromCodes(
          tr.departureFlightAirportCodes
        ),
        returnFlightLabel: tr.returnFlightAirportCodes
          ? flightLabelFromCodes(tr.returnFlightAirportCodes)
          : " ",
        departureTicketPrice: depPrice,
        returnTicketPrice: retPrice,
        seatSelectionCost: seat,
        baggageCost: baggage,
        total,
        commission,
        netAmount,
        
        bookingNumber: tr.pnrCode ?? '',
        bookingDateTime: tr.bookingDateTime ?? null,
        departureDate: tr.departureDate ?? null,
        returningDate: tr.returningDate ?? null,
      });
    }
  }
  return rows;
}

const slice = createSlice({
  name: "internalSales/list",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setResponse(state, action: PayloadAction<SalesApiResponse>) {
      state.response = action.payload;
      state.rows = toRows(action.payload.reservations?.content || []);
    },
    clear(state) {
      Object.assign(state, initialState);
    },
  },
});

export const { setLoading, setError, setResponse, clear } = slice.actions;
export default slice.reducer;

export const fetchInternalSales =
  (params: SalesParams) => async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true));
      const data = await InternalSalesService.getSales(params);
      dispatch(setResponse(data));
      dispatch(setLoading(false));
    } catch (err: any) {
      dispatch(setLoading(false));
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to fetch internal sales.";
      dispatch(setError(message));
      dispatch(errorMsg(message));
    }
  };
